import { useMsal } from '@azure/msal-react';

import React, { useContext, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import BusinessIcon from '@mui/icons-material/Business';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import { Avatar, Divider, FormControl, IconButton, InputLabel, ListItemText, Menu, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';

import { LOG_LEVEL_OPTIONS, LogLevelName, createScopedLogger, getApplicationLogLevel, setApplicationLogLevel, useApiClient } from '@@common';

import { TenantViewModel, UserClient, UserViewModel } from '../../api/api';
import { UserClaims } from '../../common/UserClaims';
import { useUserClaim } from '../../hooks/useUserClaim';
import { usePortalTheme } from '../../themes/PortalThemeProvider';
import { PortalThemeName, portalThemeOptions } from '../../themes/default';
import AuthContext from '../AuthContext';
import { useCurrentUserPermissions } from '../CurrentUserPermissionsContext';
import MenuListItem from './MenuListItem';
import TenantSelectorDialog from './TenantSelectorDialog';

interface MenuListItemAvatarProps {
	open: boolean;
	additionalSx?: object;
}

const logger = createScopedLogger('MenuListItemAvatar');

const MenuListItemAvatar: React.FC<MenuListItemAvatarProps> = ({ open, additionalSx = {} }) => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [tenantDialogOpen, setTenantDialogOpen] = React.useState(false);
	const [logLevel, setLogLevel] = React.useState<LogLevelName>(() => getApplicationLogLevel());
	const [userData, setUserData] = React.useState<UserViewModel | null>(null);
	const loadedAccountUsername = React.useRef<string | null>(null);
	const { accounts, inProgress } = useMsal();
	const auth = useContext(AuthContext);
	const { refresh: refreshPermissions } = useCurrentUserPermissions();
	const userClient = useApiClient(UserClient);
	const { themeName, setThemeName } = usePortalTheme();

	const account = useMemo(() => (accounts.length > 0 ? accounts[0] : undefined), [accounts]);

	// Check if user has the claim to change tenant
	const canChangeTenantId = useUserClaim(UserClaims.CanChangeTenantId);

	// Load user data including current tenant when account is available
	useEffect(() => {
		const loadUserData = async () => {
			// Wait for MSAL to finish initialization and ensure we have an account
			if (!account || inProgress !== 'none') {
				return;
			}

			// Only load if user can change tenant
			if (!canChangeTenantId) {
				return;
			}

			// Prevent loading if already loaded for this account
			if (loadedAccountUsername.current === account.username) {
				logger.debug('User data already loaded for account:', account.username);
				return;
			}

			// Set the flag immediately to prevent duplicate calls (especially in StrictMode)
			loadedAccountUsername.current = account.username;

			try {
				logger.debug('Loading user data for account:', account.username);
				const user = await userClient.getUser(undefined);
				setUserData(user);
			} catch (error) {
				logger.error('Error loading user data:', error);
				// Reset the flag on error so it can retry later
				loadedAccountUsername.current = null;
			}
		};

		loadUserData();
	}, [account, account?.username, canChangeTenantId, inProgress, userClient]);

	const handleOpenUserMenu = (event?: React.MouseEvent<HTMLElement>) => {
		if (event) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleCloseUserMenu = () => {
		setAnchorEl(null);
	};

	const handleSignIn = async () => {
		// https://learn.microsoft.com/en-us/entra/identity-platform/msal-js-prompt-behavior
		handleCloseUserMenu();
		await auth.authService.signIn();
	};

	const handleSignOut = async () => {
		handleCloseUserMenu();
		await auth.authService.signOut();
	};

	const handleChangeTenant = () => {
		handleCloseUserMenu();
		setTenantDialogOpen(true);
	};

	const handleTenantChanged = async (tenant: TenantViewModel) => {
		// Update the user data with the new tenant
		if (userData) {
			setUserData({
				...userData,
				tenant: tenant,
			});
		}
		await refreshPermissions();

		// Reset loaded account username so user data will be reloaded
		loadedAccountUsername.current = null;

		// A client-side navigation retains the old tenant's SignalR token and query caches.
		// Reload so notifications and presence reconnect with the newly selected tenant.
		window.location.assign('/');
	};

	const handleLogLevelChange = (event: SelectChangeEvent) => {
		const nextLevel = event.target.value as LogLevelName;
		setLogLevel(nextLevel);
		setApplicationLogLevel(nextLevel);
	};

	const handleThemeChange = (event: SelectChangeEvent) => {
		setThemeName(event.target.value as PortalThemeName);
	};
	const avatarSx = { width: 32, height: 32, bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700, fontSize: '0.875rem' };

	const avatarIcon = account ? (
		<Tooltip title="Open user menu">
			<IconButton sx={{ p: 0 }}>
				<Avatar alt={account.name} sx={avatarSx}>{account.name?.trim().charAt(0).toUpperCase()}</Avatar>
			</IconButton>
		</Tooltip>
	) : (
		<Tooltip title="Login">
			<IconButton sx={{ p: 0 }}>
				<Avatar sx={avatarSx}>
					<PersonIcon fontSize="small" />
				</Avatar>
			</IconButton>
		</Tooltip>
	);

	const handleClick = account ? handleOpenUserMenu : handleSignIn;

	return (
		<>
			<MenuListItem
				open={open}
				icon={avatarIcon}
				primaryText={account?.name || 'Login'}
				onClick={handleClick}
				additionalSx={additionalSx}
				primaryTextSx={account ? { fontWeight: 600, fontSize: '14px' } : undefined}
			/>

			{account && (
				<Menu
					id="menu-appbar"
					anchorEl={anchorEl}
					anchorOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
					keepMounted
					transformOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					open={Boolean(anchorEl)}
					onClose={handleCloseUserMenu}
				>
					<MenuItem key="profile" component={RouterLink} to="/profile" onClick={handleCloseUserMenu}>
						<PersonIcon fontSize="small" sx={{ mr: 1.5 }} />
						<ListItemText
							primary={account.name || 'Profile'}
							secondary={account.username}
							primaryTypographyProps={{ sx: { textAlign: 'left' } }}
							secondaryTypographyProps={{ sx: { textAlign: 'left' } }}
						/>
					</MenuItem>
					<MenuItem key="sign-out" onClick={handleSignOut}>
						<LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
						<Typography sx={{ textAlign: 'left' }}>Sign Out</Typography>
					</MenuItem>
					<Divider />
					<MenuItem key="theme" disableRipple sx={{ alignItems: 'flex-start', gap: 1.5, py: 1.5 }}>
						<PaletteOutlinedIcon fontSize="small" sx={{ mt: 1 }} />
						<FormControl size="small" variant="standard" sx={{ minWidth: 180 }}>
							<InputLabel id="portal-theme-label">Theme</InputLabel>
							<Select labelId="portal-theme-label" value={themeName} onChange={handleThemeChange} onClick={(event) => event.stopPropagation()} label="Theme">
								{portalThemeOptions.map((theme) => (
									<MenuItem key={theme.value} value={theme.value}>
										{theme.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</MenuItem>
					{canChangeTenantId && <Divider />}
					{canChangeTenantId && userData?.tenant && (
						<MenuItem key="current-tenant" onClick={handleCloseUserMenu} disabled>
							<BusinessIcon fontSize="small" sx={{ mr: 1.5 }} />
							<Typography sx={{ textAlign: 'left' }}>Tenant: {userData.tenant.code}</Typography>
						</MenuItem>
					)}
					{canChangeTenantId && (
						<MenuItem key="change-tenant" onClick={handleChangeTenant}>
							<ManageAccountsIcon fontSize="small" sx={{ mr: 1.5 }} />
							<Typography sx={{ textAlign: 'left' }}>Change Tenant</Typography>
						</MenuItem>
					)}
					<Divider />
					<MenuItem key="advanced-log-level" disableRipple sx={{ alignItems: 'flex-start', gap: 1.5, py: 1.5 }}>
						<SettingsIcon fontSize="small" sx={{ mt: 1 }} />
						<FormControl size="small" variant="standard" sx={{ minWidth: 180 }}>
							<InputLabel id="portal-log-level-label">Advanced Log Level</InputLabel>
							<Select
								labelId="portal-log-level-label"
								value={logLevel}
								onChange={handleLogLevelChange}
								onClick={(event) => event.stopPropagation()}
								label="Advanced Log Level"
							>
								{LOG_LEVEL_OPTIONS.map((level) => (
									<MenuItem key={level} value={level}>
										{level}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</MenuItem>
				</Menu>
			)}

			<TenantSelectorDialog open={tenantDialogOpen} onClose={() => setTenantDialogOpen(false)} onTenantChanged={handleTenantChanged} />
		</>
	);
};

export default MenuListItemAvatar;
